import { Alert, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { StorageRoom } from '../../services/storageRoom.service';
import {
  STORAGE_SEARCH_MIN_ROOMS,
  buildStorageRoomSelectLabels,
  formatStorageRoomSelectedHint,
  formatStorageRoomSelectOption,
  shouldShowStorageRoomFullLabel,
  truncateForSelectOption,
} from '../../utils/storageRoomSearch';
import { softBreakIdentifier } from '../../utils/softBreakIdentifier';
import { SAMPLE_STORAGE_SETUP_HREF } from '../../utils/sampleStorageSetupGate';
import { SampleFormSectionTitle } from './SampleFormSectionTitle';

interface SampleFormStorageFieldsProps {
  storageSearch: string;
  onStorageSearchChange: (value: string) => void;
  onClearSearch: () => void;
  storageRoomsError: boolean;
  storageRoomsPending: boolean;
  /** 取得失敗時の再試行（未指定なら再試行ボタンなし） */
  onRetryStorageRooms?: () => void;
  /** 検索フィルタ後の候補 */
  filteredStorageRooms: StorageRoom[];
  /** ラベル生成用の全件（検索で絞ると所有者付けが崩れるのを防ぐ） */
  allStorageRooms?: StorageRoom[];
  /** 検索 UI 表示判定用の総件数 */
  totalStorageRoomCount: number;
  selectedStorageRoomId: number | undefined;
  onStorageRoomChange: (roomId: number | undefined) => void;
  isInvalid: boolean;
  invalidMessage?: string;
}

/** サンプル登録・編集モーダルの保管庫検索・選択 */
export default function SampleFormStorageFields({
  storageSearch,
  onStorageSearchChange,
  onClearSearch,
  storageRoomsError,
  storageRoomsPending,
  onRetryStorageRooms,
  filteredStorageRooms,
  allStorageRooms,
  totalStorageRoomCount,
  selectedStorageRoomId,
  onStorageRoomChange,
  isInvalid,
  invalidMessage,
}: SampleFormStorageFieldsProps) {
  const { t, i18n } = useTranslation();
  const showSearch =
    totalStorageRoomCount > STORAGE_SEARCH_MIN_ROOMS || storageSearch.trim().length > 0;
  const selectedRoom =
    filteredStorageRooms.find((r) => r.id === selectedStorageRoomId) ?? null;
  const selectLabels = buildStorageRoomSelectLabels(
    allStorageRooms && allStorageRooms.length > 0
      ? allStorageRooms
      : filteredStorageRooms,
    i18n.language
  );

  return (
    <div className="sample-form-section">
      <SampleFormSectionTitle icon="fa-warehouse">
        {t('sample.storage_information')}
      </SampleFormSectionTitle>
      <Form.Group className="mb-3">
        {showSearch ? (
          <>
            <Form.Label htmlFor="storage-search" className="fw-semibold">
              {t('sample.search_storage')}
            </Form.Label>
            <div className="input-group mb-2">
              <span className="input-group-text">
                <i className="fas fa-search" aria-hidden="true" />
              </span>
              <Form.Control
                id="storage-search"
                name="storageSearch"
                type="search"
                placeholder={t('sample.search_storage')}
                aria-label={t('sample.search_storage')}
                value={storageSearch}
                onChange={(e) => onStorageSearchChange(e.target.value)}
                autoComplete="off"
              />
              {storageSearch ? (
                <Button
                  variant="outline-secondary"
                  type="button"
                  aria-label={t('common.clear')}
                  onClick={onClearSearch}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
        {storageRoomsError ? (
          <Alert variant="danger" className="py-2 small mb-2 d-flex flex-wrap align-items-center gap-2">
            <span className="me-auto">{t('storage_room.load_failed')}</span>
            {onRetryStorageRooms ? (
              <Button
                variant="outline-danger"
                size="sm"
                type="button"
                disabled={storageRoomsPending}
                onClick={onRetryStorageRooms}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </Button>
            ) : null}
          </Alert>
        ) : null}
        {storageRoomsPending && !storageRoomsError ? (
          <Form.Text className="text-muted d-block mb-2">{t('common.loading')}</Form.Text>
        ) : null}
        {!storageRoomsPending && !storageRoomsError && filteredStorageRooms.length === 0 ? (
          storageSearch ? (
            <Form.Text className="text-muted d-block mb-2">
              {t('sample.storage_search_no_results')}
            </Form.Text>
          ) : (
            <Alert variant="warning" className="py-2 small mb-2">
              <p className="mb-2">
                {t('sample.list_empty_needs_storage', {
                  defaultValue: 'サンプルを登録する前に、保管庫を1つ登録してください。',
                })}
              </p>
              <Button as={Link as any} to={SAMPLE_STORAGE_SETUP_HREF} variant="outline-primary" size="sm">
                <i className="fas fa-warehouse me-1" aria-hidden="true" />
                {t('sample.list_empty_go_to_storage', {
                  defaultValue: '保管庫を登録する',
                })}
              </Button>
            </Alert>
          )
        ) : null}
        <Form.Label htmlFor="storage-select" className="fw-semibold">
          {t('sample.storage_room')} <span className="text-danger">*</span>
        </Form.Label>
        <Form.Select
          id="storage-select"
          name="storageRoomId"
          value={selectedStorageRoomId || ''}
          onChange={(e) => {
            const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
            onStorageRoomChange(value);
          }}
          isInvalid={isInvalid}
          disabled={storageRoomsPending || storageRoomsError}
          required
          aria-required="true"
          autoComplete="off"
          className="border sample-storage-select"
          aria-describedby={
            shouldShowStorageRoomFullLabel(selectedRoom, t) ? 'storage-select-full' : undefined
          }
        >
          <option value="">{t('sample.select_please')}</option>
          {filteredStorageRooms.map((room) => {
            const label = formatStorageRoomSelectOption(room, selectLabels, t);
            return (
              <option key={room.id} value={room.id} title={label}>
                {truncateForSelectOption(label)}
              </option>
            );
          })}
        </Form.Select>
        {shouldShowStorageRoomFullLabel(selectedRoom, t) ? (
          <Form.Text id="storage-select-full" className="text-muted d-block mt-1 text-break">
            {softBreakIdentifier(formatStorageRoomSelectedHint(selectedRoom!, t) || '', t)}
          </Form.Text>
        ) : null}
        <Form.Control.Feedback type="invalid">{invalidMessage}</Form.Control.Feedback>
      </Form.Group>
    </div>
  );
}
